import type { CvCheckInput, CvCheckRule } from './types';
import { CLICHES, DUTY_OPENERS, FIRST_PERSON, WORK_LANGUAGE_NAMES } from './phrases';
import { isOngoingEnd, parseCvMonth } from './parse-cv-date';

/**
 * The CV check rules (TEC-711 phase 2, rules-only first cut). Weights per category:
 * essentials 20, impact 30, Finnish fit 20, clarity 15, tone 15. These are a starting point:
 * the spec calls for calibrating them on real CVs so an ordinary solid CV lands around 70-80.
 *
 * Rules are pure functions of the profile form, so the check runs in the browser on every edit
 * with no network call. Each rule can return null ("doesn't apply"), which removes its points
 * from the maximum instead of punishing the CV, e.g. there is no quantified-results rule for
 * someone with no work history yet.
 */

const MIN_DESCRIPTION_CHARS = 25;
const MIN_SUMMARY_CHARS = 80;
const MAX_SUMMARY_CHARS = 600;
const MAX_HEADLINE_CHARS = 80;
/** Rough text budget for two A4 pages in the templates, measured on the six example CVs. */
const TWO_PAGE_CHAR_BUDGET = 6000;
const GAP_MONTHS = 12;
/** Below this much text the length rule doesn't apply yet (the example CVs are 1,500+). */
const MIN_LENGTH_CHARS = 600;
const GENERIC_HEADLINES = new Set([
  'työnhakija',
  'työtön työnhakija',
  'työntekijä',
  'cv',
  'ansioluettelo',
  'job seeker',
  'jobseeker',
  'employee',
  'resume',
  'curriculum vitae',
  'arbetssökande',
  'meritförteckning',
]);
const FIRST_PERSON_LIMIT = 5;
/** Roles that ended longer ago than this don't need a description (calibrated on the example CVs). */
const RECENT_ROLE_MONTHS = 10 * 12;
const MAX_RECENT_ROLES = 4;

/**
 * The roles a reader expects detail on: ongoing ones and those that ended in the last ten years,
 * at most the four most recent. A role with unreadable dates counts as recent. When every role is
 * older than that, the newest one still counts.
 */
export function recentRoles<T extends { startDate: string; endDate: string }>(roles: readonly T[], now: Date = new Date()): T[] {
  const nowIndex = now.getFullYear() * 12 + now.getMonth();
  const endOf = (e: T): number | null => (isOngoingEnd(e.endDate) ? nowIndex : parseCvMonth(e.endDate, 'end') ?? parseCvMonth(e.startDate, 'end'));
  const dated = roles.map((role) => ({ role, end: endOf(role) }));
  const recent = dated.filter((d) => d.end === null || nowIndex - d.end <= RECENT_ROLE_MONTHS);
  const pool = recent.length > 0 ? recent : dated.slice().sort((a, b) => (b.end ?? 0) - (a.end ?? 0)).slice(0, 1);
  return pool
    .sort((a, b) => (b.end ?? nowIndex) - (a.end ?? nowIndex))
    .slice(0, MAX_RECENT_ROLES)
    .map((d) => d.role);
}

function nonEmpty(s: string | undefined | null): boolean {
  return Boolean(s && s.trim());
}

function descriptions(input: CvCheckInput): string[] {
  return input.cv.profileWorkExperiences.map((e) => (e.description ?? '').trim()).filter(Boolean);
}

function allText(input: CvCheckInput): string {
  const { cv } = input;
  return [
    cv.profileTitle,
    cv.profileSummary,
    ...cv.profileWorkExperiences.flatMap((e) => [e.title, e.company, e.description ?? '']),
    ...cv.profileEducation.flatMap((e) => [e.degree, e.institution]),
    ...cv.profileProjects.flatMap((p) => [p.title, p.description]),
    ...cv.profileSkills,
    ...input.licences,
  ]
    .filter(Boolean)
    .join('\n');
}

/** Text the person wrote in their own voice (where tone matters), as opposed to names and dates. */
function proseText(input: CvCheckInput): string {
  const { cv } = input;
  return [cv.profileTitle, cv.profileSummary, ...descriptions(input), ...cv.profileProjects.map((p) => p.description)]
    .filter(Boolean)
    .join('\n');
}

function ratio(part: number, whole: number): number {
  return whole === 0 ? 1 : Math.max(0, Math.min(1, part / whole));
}

export const CV_CHECK_RULES: readonly CvCheckRule[] = [
  // Essentials (20)
  {
    id: 'name',
    category: 'essentials',
    maxPoints: 3,
    sectionId: 'section-applying',
    evaluate: (input) => ({ earned: nonEmpty(input.firstName) && nonEmpty(input.lastName) ? 3 : 0 }),
  },
  {
    id: 'contact',
    category: 'essentials',
    maxPoints: 3,
    sectionId: 'section-applying',
    evaluate: (input) => {
      const have = Number(nonEmpty(input.email)) + Number(nonEmpty(input.phone));
      return { earned: have === 2 ? 3 : have === 1 ? 1.5 : 0 };
    },
  },
  {
    id: 'headline',
    category: 'essentials',
    maxPoints: 4,
    sectionId: 'section-header',
    evaluate: (input) => {
      const headline = input.cv.profileTitle.trim();
      if (!headline) return { earned: 0 };
      // "Job seeker" says nothing about the work; half points until it names a role or field.
      return { earned: GENERIC_HEADLINES.has(headline.toLowerCase().replace(/[.!]+$/, '')) ? 2 : 4 };
    },
  },
  {
    id: 'summary',
    category: 'essentials',
    maxPoints: 4,
    sectionId: 'section-header',
    evaluate: (input) => {
      const len = input.cv.profileSummary.trim().length;
      return { earned: len >= MIN_SUMMARY_CHARS ? 4 : len > 0 ? 2 : 0 };
    },
  },
  {
    id: 'experienceOrEducation',
    category: 'essentials',
    maxPoints: 3,
    sectionId: 'section-experience',
    evaluate: (input) => {
      const { cv } = input;
      const hasWork = cv.profileWorkExperiences.some((e) => nonEmpty(e.title) || nonEmpty(e.company));
      // A student or graduate with education and projects has a complete CV without work history.
      const hasStudyPath = cv.profileEducation.length > 0 && cv.profileProjects.length > 0;
      return { earned: hasWork || hasStudyPath ? 3 : 0 };
    },
  },
  {
    id: 'education',
    category: 'essentials',
    maxPoints: 3,
    sectionId: 'section-education',
    evaluate: (input) => ({ earned: input.cv.profileEducation.some((e) => nonEmpty(e.degree) || nonEmpty(e.institution)) ? 3 : 0 }),
  },

  // Impact (30)
  {
    id: 'roleDescriptions',
    category: 'impact',
    maxPoints: 8,
    sectionId: 'section-experience',
    evaluate: (input) => {
      const roles = recentRoles(input.cv.profileWorkExperiences);
      if (roles.length === 0) return null;
      const described = roles.filter((e) => (e.description ?? '').trim().length >= MIN_DESCRIPTION_CHARS).length;
      return { earned: 8 * ratio(described, roles.length), values: { missing: roles.length - described } };
    },
  },
  {
    id: 'quantifiedResults',
    category: 'impact',
    maxPoints: 10,
    sectionId: 'section-experience',
    evaluate: (input) => {
      const texts = descriptions(input);
      if (texts.length === 0) return null;
      const withNumbers = texts.filter((t) => /\d/.test(t)).length;
      // Full points once at least half the described roles show a concrete number or result.
      const target = Math.max(1, Math.ceil(texts.length / 2));
      return { earned: 10 * ratio(withNumbers, target), values: { withNumbers, total: texts.length } };
    },
  },
  {
    id: 'dutyPhrasing',
    category: 'impact',
    maxPoints: 6,
    sectionId: 'section-experience',
    evaluate: (input) => {
      const texts = descriptions(input);
      if (texts.length === 0) return null;
      const openers = DUTY_OPENERS[input.cvLocale];
      const dutyCount = texts.filter((t) => {
        const lower = t.toLowerCase();
        return openers.some((o) => lower.startsWith(o));
      }).length;
      return { earned: 6 * (1 - ratio(dutyCount, texts.length)), values: { count: dutyCount } };
    },
  },
  {
    id: 'skillsCount',
    category: 'impact',
    maxPoints: 6,
    sectionId: 'section-skills',
    evaluate: (input) => {
      const count = input.cv.profileSkills.filter((s) => s.trim()).length;
      return { earned: count >= 6 ? 6 : count >= 3 ? 3 : 0, values: { count } };
    },
  },

  // Finnish fit (20)
  {
    id: 'languagesListed',
    category: 'finnishFit',
    maxPoints: 6,
    sectionId: 'section-languages',
    evaluate: (input) => {
      const count = input.languages.filter((l) => l.language.trim()).length;
      return { earned: count >= 2 ? 6 : count === 1 ? 3 : 0, values: { count } };
    },
  },
  {
    id: 'workLanguage',
    category: 'finnishFit',
    maxPoints: 4,
    sectionId: 'section-languages',
    evaluate: (input) => {
      const has = input.languages.some((l) => WORK_LANGUAGE_NAMES.includes(l.language.trim().toLowerCase()));
      return { earned: has ? 4 : 0 };
    },
  },
  {
    id: 'length',
    category: 'finnishFit',
    maxPoints: 5,
    sectionId: 'section-experience',
    evaluate: (input) => {
      const chars = allText(input).length;
      // Too little text to judge length yet: the content rules carry that CV, not free points here.
      if (chars < MIN_LENGTH_CHARS) return null;
      return { earned: chars <= TWO_PAGE_CHAR_BUDGET ? 5 : 0, values: { chars } };
    },
  },
  {
    id: 'datesComplete',
    category: 'finnishFit',
    maxPoints: 5,
    sectionId: 'section-experience',
    evaluate: (input) => {
      const roles = input.cv.profileWorkExperiences;
      const education = input.cv.profileEducation;
      const entries = roles.length + education.length;
      if (entries === 0) return null;
      // A degree is often listed by its graduation year alone, so either date counts for education.
      const dated =
        roles.filter((e) => parseCvMonth(e.startDate) !== null).length +
        education.filter((e) => parseCvMonth(e.startDate) !== null || parseCvMonth(e.endDate, 'end') !== null).length;
      return { earned: 5 * ratio(dated, entries), values: { missing: entries - dated } };
    },
  },

  // Clarity (15)
  {
    id: 'gaps',
    category: 'clarity',
    maxPoints: 5,
    sectionId: 'section-experience',
    evaluate: (input) => {
      const spans = input.cv.profileWorkExperiences
        .map((e) => {
          const start = parseCvMonth(e.startDate, 'start');
          if (start === null) return null;
          const end = isOngoingEnd(e.endDate) ? Number.POSITIVE_INFINITY : parseCvMonth(e.endDate, 'end');
          return end === null ? null : { start, end };
        })
        .filter((s): s is { start: number; end: number } => s !== null)
        .sort((a, b) => a.start - b.start);
      if (spans.length < 2) return null;
      let gaps = 0;
      let coveredUntil = spans[0].end;
      for (const span of spans.slice(1)) {
        if (span.start - coveredUntil > GAP_MONTHS) gaps += 1;
        coveredUntil = Math.max(coveredUntil, span.end);
      }
      return { earned: gaps === 0 ? 5 : 0, values: { count: gaps } };
    },
  },
  {
    id: 'summaryLength',
    category: 'clarity',
    maxPoints: 5,
    sectionId: 'section-header',
    evaluate: (input) => {
      const len = input.cv.profileSummary.trim().length;
      // A short or missing profile is the summary rule's finding; this one only flags too long.
      if (len < MIN_SUMMARY_CHARS) return null;
      return { earned: len <= MAX_SUMMARY_CHARS ? 5 : 0, values: { chars: len } };
    },
  },
  {
    id: 'headlineLength',
    category: 'clarity',
    maxPoints: 5,
    sectionId: 'section-header',
    evaluate: (input) => {
      const headline = input.cv.profileTitle.trim();
      if (!headline) return null;
      const shouting = headline.length > 4 && headline === headline.toUpperCase() && /\p{L}/u.test(headline);
      return { earned: headline.length <= MAX_HEADLINE_CHARS && !shouting ? 5 : 0 };
    },
  },

  // Tone (15)
  {
    id: 'cliches',
    category: 'tone',
    maxPoints: 10,
    sectionId: 'section-header',
    evaluate: (input) => {
      const text = proseText(input);
      if (!text) return null;
      const lower = text.toLowerCase();
      const found = CLICHES[input.cvLocale].filter((c) => lower.includes(c));
      // Quote the words as the person wrote them ("tiimipelaaja"), not the stem we matched on.
      const examples = found.map((stem) => {
        const escaped = stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return text.match(new RegExp(`[\\p{L}-]*${escaped}[\\p{L}-]*`, 'iu'))?.[0] ?? stem;
      });
      const earned = found.length === 0 ? 10 : found.length === 1 ? 6 : found.length === 2 ? 3 : 0;
      return { earned, values: { count: found.length, examples: examples.slice(0, 3).join(', ') } };
    },
  },
  {
    id: 'firstPerson',
    category: 'tone',
    maxPoints: 5,
    sectionId: 'section-header',
    evaluate: (input) => {
      const text = proseText(input);
      if (!text) return null;
      const count = (text.match(FIRST_PERSON[input.cvLocale]) ?? []).length;
      return { earned: count > FIRST_PERSON_LIMIT ? 0 : 5, values: { count } };
    },
  },
];

export { allText as cvCheckAllText };
